import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { useAboutContent, useUpdateContent } from '@/hooks/content-hooks';

/* ── Types ────────────────────────────────────────────────────────────── */
type ValueItem = { title: string; description: string };
type TeamMember = { name: string; role: string; bio: string };
type HourRow = { day: string; time: string };

function parseJson<T>(raw: string | undefined, fallback: T[]): T[] {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T[]; } catch { return fallback; }
}

/* ── Reusable card shell ─────────────────────────────────────────────── */
const EditCard: React.FC<{
  title: string;
  lastSaved?: string;
  onReset: () => void;
  onPublish: () => void;
  saving: boolean;
  children: React.ReactNode;
}> = ({ title, lastSaved, onReset, onPublish, saving, children }) => (
  <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--lib-hairline)] bg-[var(--lib-surface-2)]">
      <h3 className="text-sm font-semibold text-[var(--lib-ink)]">{title}</h3>
      <span className="text-xs text-[var(--lib-ink-muted)] font-mono">
        {lastSaved ? `Saved ${lastSaved}` : 'Not yet saved'}
      </span>
    </div>
    {children}
    <div className="px-5 py-3 border-t border-[var(--lib-hairline)] flex justify-end gap-2">
      <button
        onClick={onReset}
        className="px-4 py-1.5 text-sm text-[var(--lib-ink-muted)] border border-[var(--lib-hairline)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
      >
        Reset
      </button>
      <button
        onClick={onPublish}
        disabled={saving}
        className="bg-[var(--lib-gold)] hover:brightness-105 disabled:opacity-50 text-[var(--lib-on-gold)] font-medium text-sm rounded-lg px-4 py-1.5 transition-colors"
      >
        {saving ? 'Saving…' : 'Publish'}
      </button>
    </div>
  </div>
);

/* ── Two-column current / edit layout ───────────────────────────────── */
const TwoCol: React.FC<{
  current: string;
  draft: string;
  rows?: number;
  onChange: (v: string) => void;
}> = ({ current, draft, rows = 4, onChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[var(--lib-hairline)]">
    <div className="p-5 bg-[var(--lib-surface-2)]">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-2">Current</div>
      <p className="text-sm text-[var(--lib-ink-muted)] leading-relaxed whitespace-pre-wrap">
        {current || '(empty)'}
      </p>
    </div>
    <div className="p-5">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-2">Edit</div>
      <textarea
        value={draft}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] text-[var(--lib-ink)] bg-transparent resize-none focus:outline-none focus:border-[var(--lib-gold)]"
      />
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════ */

const SiteContentModule: React.FC = () => {
  const { data: content, isLoading } = useAboutContent();
  const { mutate: save, isPending } = useUpdateContent('about');

  /* ── Draft state (all state vars preserved from original) ─────────── */
  const [hero, setHero] = useState({ kicker_label: '', tagline: '' });
  const [heroDraft, setHeroDraft] = useState({ kicker_label: '', tagline: '' });

  const [mission, setMission] = useState({ paragraph_1: '', paragraph_2: '', established: '', location: '' });
  const [missionDraft, setMissionDraft] = useState({ paragraph_1: '', paragraph_2: '', established: '', location: '' });

  const [stats, setStats] = useState({ titles: '', book_clubs: '', active_members: '', events_per_year: '' });
  const [statsDraft, setStatsDraft] = useState({ titles: '', book_clubs: '', active_members: '', events_per_year: '' });

  const [values, setValues] = useState<ValueItem[]>([]);
  const [valuesDraft, setValuesDraft] = useState<ValueItem[]>([]);

  const [team, setTeam] = useState<TeamMember[]>([]);
  const [teamDraft, setTeamDraft] = useState<TeamMember[]>([]);

  const [hours, setHours] = useState<HourRow[]>([]);
  const [hoursDraft, setHoursDraft] = useState<HourRow[]>([]);

  const [contact, setContact] = useState({ phone: '', email: '', address: '', maps_url: '' });
  const [contactDraft, setContactDraft] = useState({ phone: '', email: '', address: '', maps_url: '' });

  const [cta, setCta] = useState({ description: '' });
  const [ctaDraft, setCtaDraft] = useState({ description: '' });

  useEffect(() => {
    if (!content) return;
    const c = (k: string) => content[k] ?? '';

    const h = { kicker_label: c('about.hero.kicker_label'), tagline: c('about.hero.tagline') };
    setHero(h); setHeroDraft(h);

    const m = {
      paragraph_1: c('about.mission.paragraph_1'),
      paragraph_2: c('about.mission.paragraph_2'),
      established: c('about.mission.established'),
      location: c('about.mission.location'),
    };
    setMission(m); setMissionDraft(m);

    const s = {
      titles: c('about.stats.titles'),
      book_clubs: c('about.stats.book_clubs'),
      active_members: c('about.stats.active_members'),
      events_per_year: c('about.stats.events_per_year'),
    };
    setStats(s); setStatsDraft(s);

    const v = parseJson<ValueItem>(c('about.values.items'), []);
    setValues(v); setValuesDraft(v);

    const t = parseJson<TeamMember>(c('about.team.members'), []);
    setTeam(t); setTeamDraft(t);

    const hr = parseJson<HourRow>(c('about.hours.schedule'), []);
    setHours(hr); setHoursDraft(hr);

    const ct = {
      phone: c('about.contact.phone'),
      email: c('about.contact.email'),
      address: c('about.contact.address'),
      maps_url: c('about.contact.maps_url'),
    };
    setContact(ct); setContactDraft(ct);

    const cta_ = { description: c('about.cta.description') };
    setCta(cta_); setCtaDraft(cta_);
  }, [content]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-[var(--lib-ink-muted)] text-sm">
        Loading content…
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div>
        <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)]">Site Content</h1>
        <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Edit public-facing page copy.</p>
      </div>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <EditCard
        title="Hero"
        onReset={() => setHeroDraft(hero)}
        onPublish={() => {
          save({ 'about.hero.kicker_label': heroDraft.kicker_label, 'about.hero.tagline': heroDraft.tagline });
          setHero(heroDraft);
        }}
        saving={isPending}
      >
        <div className="border-b border-[var(--lib-hairline)]">
          <div className="px-5 pt-4 pb-1 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Kicker Label</div>
          <TwoCol
            current={hero.kicker_label}
            draft={heroDraft.kicker_label}
            rows={2}
            onChange={v => setHeroDraft(p => ({ ...p, kicker_label: v }))}
          />
        </div>
        <div>
          <div className="px-5 pt-4 pb-1 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Tagline</div>
          <TwoCol
            current={hero.tagline}
            draft={heroDraft.tagline}
            rows={3}
            onChange={v => setHeroDraft(p => ({ ...p, tagline: v }))}
          />
        </div>
      </EditCard>

      {/* ── Mission ────────────────────────────────────────────────────── */}
      <EditCard
        title="Mission"
        onReset={() => setMissionDraft(mission)}
        onPublish={() => {
          save({
            'about.mission.paragraph_1': missionDraft.paragraph_1,
            'about.mission.paragraph_2': missionDraft.paragraph_2,
            'about.mission.established': missionDraft.established,
            'about.mission.location': missionDraft.location,
          });
          setMission(missionDraft);
        }}
        saving={isPending}
      >
        {[
          { key: 'paragraph_1' as const, label: 'Paragraph 1', rows: 4 },
          { key: 'paragraph_2' as const, label: 'Paragraph 2', rows: 4 },
          { key: 'established' as const, label: 'Established', rows: 1 },
          { key: 'location' as const, label: 'Location Tag', rows: 1 },
        ].map(({ key, label, rows }, idx, arr) => (
          <div key={key} className={idx < arr.length - 1 ? 'border-b border-[var(--lib-hairline)]' : ''}>
            <div className="px-5 pt-4 pb-1 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">{label}</div>
            <TwoCol
              current={mission[key]}
              draft={missionDraft[key]}
              rows={rows}
              onChange={v => setMissionDraft(p => ({ ...p, [key]: v }))}
            />
          </div>
        ))}
      </EditCard>

      {/* ── Stats ──────────────────────────────────────────────────────── */}
      <EditCard
        title="Stats"
        onReset={() => setStatsDraft(stats)}
        onPublish={() => {
          save({
            'about.stats.titles': statsDraft.titles,
            'about.stats.book_clubs': statsDraft.book_clubs,
            'about.stats.active_members': statsDraft.active_members,
            'about.stats.events_per_year': statsDraft.events_per_year,
          });
          setStats(statsDraft);
        }}
        saving={isPending}
      >
        {[
          { key: 'titles' as const, label: 'Titles' },
          { key: 'book_clubs' as const, label: 'Book Clubs' },
          { key: 'active_members' as const, label: 'Active Members' },
          { key: 'events_per_year' as const, label: 'Events / Year' },
        ].map(({ key, label }, idx, arr) => (
          <div key={key} className={idx < arr.length - 1 ? 'border-b border-[var(--lib-hairline)]' : ''}>
            <div className="px-5 pt-4 pb-1 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">{label}</div>
            <TwoCol
              current={stats[key]}
              draft={statsDraft[key]}
              rows={1}
              onChange={v => setStatsDraft(p => ({ ...p, [key]: v }))}
            />
          </div>
        ))}
      </EditCard>

      {/* ── Values ─────────────────────────────────────────────────────── */}
      <EditCard
        title="Values"
        onReset={() => setValuesDraft(values)}
        onPublish={() => {
          save({ 'about.values.items': JSON.stringify(valuesDraft) });
          setValues(valuesDraft);
        }}
        saving={isPending}
      >
        <div className="p-5 space-y-4">
          {valuesDraft.map((v, i) => (
            <div key={i} className="rounded-lg border border-[var(--lib-hairline)] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-[var(--lib-ink-muted)]">Value {i + 1}</span>
                <button
                  className="h-7 w-7 flex items-center justify-center text-[var(--lib-danger)] hover:text-[var(--lib-danger)] hover:bg-[var(--lib-danger)]/10 rounded-lg transition-colors"
                  onClick={() => setValuesDraft(p => p.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-1.5">
                <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Title</div>
                <input
                  value={v.title}
                  onChange={e => setValuesDraft(p => p.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))}
                  className="w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] text-[var(--lib-ink)] bg-transparent focus:outline-none focus:border-[var(--lib-gold)]"
                />
              </div>
              <div className="space-y-1.5">
                <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Description</div>
                <textarea
                  rows={2}
                  value={v.description}
                  onChange={e => setValuesDraft(p => p.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
                  className="w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] text-[var(--lib-ink)] bg-transparent resize-none focus:outline-none focus:border-[var(--lib-gold)]"
                />
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setValuesDraft(p => [...p, { title: '', description: '' }])}
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add value
          </Button>
        </div>
      </EditCard>

      {/* ── Team ───────────────────────────────────────────────────────── */}
      <EditCard
        title="Team"
        onReset={() => setTeamDraft(team)}
        onPublish={() => {
          save({
            'about.team.intro': content?.['about.team.intro'] ?? '',
            'about.team.members': JSON.stringify(teamDraft),
          });
          setTeam(teamDraft);
        }}
        saving={isPending}
      >
        <div className="p-5 space-y-4">
          {teamDraft.map((m, i) => (
            <div key={i} className="rounded-lg border border-[var(--lib-hairline)] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-[var(--lib-ink-muted)]">Member {i + 1}</span>
                <button
                  className="h-7 w-7 flex items-center justify-center text-[var(--lib-danger)] hover:text-[var(--lib-danger)] hover:bg-[var(--lib-danger)]/10 rounded-lg transition-colors"
                  onClick={() => setTeamDraft(p => p.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(['name', 'role'] as const).map(field => (
                  <div key={field} className="space-y-1.5">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">{field}</div>
                    <input
                      value={m[field]}
                      onChange={e => setTeamDraft(p => p.map((x, idx) => idx === i ? { ...x, [field]: e.target.value } : x))}
                      className="w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] text-[var(--lib-ink)] bg-transparent focus:outline-none focus:border-[var(--lib-gold)]"
                    />
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Bio</div>
                <textarea
                  rows={2}
                  value={m.bio}
                  onChange={e => setTeamDraft(p => p.map((x, idx) => idx === i ? { ...x, bio: e.target.value } : x))}
                  className="w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] text-[var(--lib-ink)] bg-transparent resize-none focus:outline-none focus:border-[var(--lib-gold)]"
                />
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTeamDraft(p => [...p, { name: '', role: '', bio: '' }])}
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add member
          </Button>
        </div>
      </EditCard>

      {/* ── Library Hours ──────────────────────────────────────────────── */}
      <EditCard
        title="Library Hours"
        onReset={() => setHoursDraft(hours)}
        onPublish={() => {
          save({ 'about.hours.schedule': JSON.stringify(hoursDraft) });
          setHours(hoursDraft);
        }}
        saving={isPending}
      >
        <div className="p-5 space-y-3">
          {hoursDraft.map((h, i) => (
            <div key={i} className="flex items-center gap-3">
              <input
                className="flex-1 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] text-[var(--lib-ink)] bg-transparent focus:outline-none focus:border-[var(--lib-gold)]"
                placeholder="Day(s)"
                value={h.day}
                onChange={e => setHoursDraft(p => p.map((x, idx) => idx === i ? { ...x, day: e.target.value } : x))}
              />
              <input
                className="flex-1 px-3 py-2 border border-[var(--lib-hairline)] rounded-md text-[13px] text-[var(--lib-ink)] bg-transparent focus:outline-none focus:border-[var(--lib-gold)]"
                placeholder="Time"
                value={h.time}
                onChange={e => setHoursDraft(p => p.map((x, idx) => idx === i ? { ...x, time: e.target.value } : x))}
              />
              <button
                className="shrink-0 h-8 w-8 flex items-center justify-center text-[var(--lib-danger)] hover:text-[var(--lib-danger)] hover:bg-[var(--lib-danger)]/10 rounded-lg transition-colors"
                onClick={() => setHoursDraft(p => p.filter((_, idx) => idx !== i))}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHoursDraft(p => [...p, { day: '', time: '' }])}
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add row
          </Button>
        </div>
      </EditCard>

      {/* ── Contact ────────────────────────────────────────────────────── */}
      <EditCard
        title="Contact"
        onReset={() => setContactDraft(contact)}
        onPublish={() => {
          save({
            'about.contact.phone': contactDraft.phone,
            'about.contact.email': contactDraft.email,
            'about.contact.address': contactDraft.address,
            'about.contact.maps_url': contactDraft.maps_url,
          });
          setContact(contactDraft);
        }}
        saving={isPending}
      >
        {[
          { key: 'phone' as const, label: 'Phone', rows: 1 },
          { key: 'email' as const, label: 'Email', rows: 1 },
          { key: 'address' as const, label: 'Address', rows: 2 },
          { key: 'maps_url' as const, label: 'Google Maps URL', rows: 1 },
        ].map(({ key, label, rows }, idx, arr) => (
          <div key={key} className={idx < arr.length - 1 ? 'border-b border-[var(--lib-hairline)]' : ''}>
            <div className="px-5 pt-4 pb-1 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">{label}</div>
            <TwoCol
              current={contact[key]}
              draft={contactDraft[key]}
              rows={rows}
              onChange={v => setContactDraft(p => ({ ...p, [key]: v }))}
            />
          </div>
        ))}
      </EditCard>

      {/* ── Closing CTA ────────────────────────────────────────────────── */}
      <EditCard
        title="Closing CTA"
        onReset={() => setCtaDraft(cta)}
        onPublish={() => {
          save({ 'about.cta.description': ctaDraft.description });
          setCta(ctaDraft);
        }}
        saving={isPending}
      >
        <TwoCol
          current={cta.description}
          draft={ctaDraft.description}
          rows={3}
          onChange={v => setCtaDraft({ description: v })}
        />
      </EditCard>
    </div>
  );
};

export default SiteContentModule;
